import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  RelationshipsState,
  Relationship,
  RelationshipsListData,
} from '../../types/relationships.types';

const initialState: RelationshipsState = {
  relationships: [],
  selectedRelationship: null,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    pageSize: 10,
    totalPages: 0,
    totalRecords: 0,
    hasMorePages: false,
  },
  search: '',
};

const relationshipsSlice = createSlice({
  name: 'relationships',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setRelationships: (state, action: PayloadAction<RelationshipsListData>) => {
      state.relationships = action.payload.relationships;
      state.pagination = {
        ...state.pagination,
        totalPages: action.payload.totalPages,
        totalRecords: action.payload.totalRecords,
        hasMorePages: action.payload.hasMorePages,
      };
      state.loading = false;
      state.error = null;
    },
    setSelectedRelationship: (state, action: PayloadAction<Relationship | null>) => {
      state.selectedRelationship = action.payload;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.page = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pagination.pageSize = action.payload;
    },
    setSearch: (state, action: PayloadAction<string>) => {
      state.search = action.payload;
      state.pagination.page = 1;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedRelationship: (state) => {
      state.selectedRelationship = null;
    },
    addRelationship: (state, action: PayloadAction<Relationship>) => {
      state.relationships.unshift(action.payload);
      state.pagination.totalRecords += 1;
    },
    updateRelationship: (state, action: PayloadAction<Relationship>) => {
      const index = state.relationships.findIndex((r) => r.id === action.payload.id);
      if (index !== -1) {
        // Preserve targetTable from existing record since update response doesn't include it
        state.relationships[index] = {
          ...state.relationships[index],
          ...action.payload,
          targetTable: action.payload.targetTable || state.relationships[index].targetTable,
        };
      }
      if (state.selectedRelationship?.id === action.payload.id) {
        state.selectedRelationship = {
          ...state.selectedRelationship,
          ...action.payload,
          targetTable: action.payload.targetTable || state.selectedRelationship.targetTable,
        };
      }
    },
    deleteRelationship: (state, action: PayloadAction<string>) => {
      state.relationships = state.relationships.filter((r) => r.id !== action.payload);
      state.pagination.totalRecords -= 1;
      if (state.selectedRelationship?.id === action.payload) {
        state.selectedRelationship = null;
      }
    },
    resetRelationships: () => initialState,
  },
});

export const {
  setLoading,
  setError,
  setRelationships,
  setSelectedRelationship,
  setPage,
  setPageSize,
  setSearch,
  clearError,
  clearSelectedRelationship,
  addRelationship,
  updateRelationship,
  deleteRelationship,
  resetRelationships,
} = relationshipsSlice.actions;

export default relationshipsSlice.reducer;
